import { ReactNode } from 'react';
import { formatLigneOrdonnance } from '@/lib/voiesMedicament';

interface ConsultationOverviewProps {
  consultation: any;
  ouvert: boolean;
  onToggle: () => void;
}

const SectionLabel = 'text-[11px] font-bold uppercase tracking-wide text-gray-500';

function Section({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
        <p className="text-sm font-bold text-gray-800">{label}</p>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

export default function ConsultationOverview({ consultation, ouvert, onToggle }: ConsultationOverviewProps) {
  const supp =
    consultation.donnees_supplementaires && typeof consultation.donnees_supplementaires === 'object'
      ? consultation.donnees_supplementaires
      : {};

  const constantes = [
    { label: 'Poids', value: consultation.poids != null ? `${consultation.poids} kg` : '', icon: 'ri-scales-3-line' },
    { label: 'Taille', value: consultation.taille != null ? `${consultation.taille} cm` : '', icon: 'ri-ruler-line' },
    { label: 'Température', value: consultation.temperature != null ? `${consultation.temperature} °C` : '', icon: 'ri-temp-hot-line' },
    { label: 'Saturation', value: consultation.saturation != null ? `${consultation.saturation} %` : '', icon: 'ri-heart-pulse-line' },
    { label: 'Tension', value: consultation.tension || '', icon: 'ri-pulse-line' },
  ].filter((c) => c.value);

  const medicamentsEnCours: any[] = Array.isArray(supp.medicamentsEnCours) ? supp.medicamentsEnCours : [];
  const medicamentsPrescrits: any[] = Array.isArray(supp.medicamentsPrescrits) ? supp.medicamentsPrescrits : [];
  const soins: any[] = Array.isArray(supp.soins) ? supp.soins : [];

  const hasTraitement =
    consultation.traitement_en_cours || medicamentsEnCours.length > 0 || consultation.patient_regulier_medicaments != null;
  const hasPrescription = medicamentsPrescrits.filter((m) => m.nom?.trim()).length > 0;
  const hasSoins = supp.showFicheSoin && soins.filter((s) => s.description?.trim()).length > 0;

  const hasAny =
    constantes.length > 0 ||
    !!consultation.symptomes_signes ||
    !!consultation.antecedents_medicaux_connus ||
    hasTraitement ||
    !!consultation.examen ||
    !!consultation.diagnostic ||
    hasPrescription ||
    hasSoins ||
    !!consultation.suivi_vers ||
    !!consultation.prochain_rdv ||
    !!consultation.mot_fin;

  const heureDeb = consultation.heure_debut ? consultation.heure_debut.substring(0, 5) : '';
  const heureFin = consultation.heure_fin ? consultation.heure_fin.substring(0, 5) : '';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer text-left"
      >
        <div className="flex items-center gap-3">
          <div>
            <p className="font-semibold text-gray-900 text-sm">Consultation complète</p>
            <p className="text-xs text-gray-500">
              {consultation.date_consultation
                ? new Date(consultation.date_consultation).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                : '—'}
              {heureDeb ? ` · ${heureDeb}` : ''}
              {heureFin ? ` – ${heureFin}` : ''}
            </p>
          </div>
        </div>
        <i className={`text-gray-400 ${ouvert ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
      </button>

      {ouvert && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          {!hasAny && (
            <p className="text-sm text-gray-400 italic">Aucun détail disponible pour cette consultation.</p>
          )}

          {/* Constantes */}
          {constantes.length > 0 && (
            <Section label="Constantes">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {constantes.map((c) => (
                  <div key={c.label} className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <p className="flex items-center gap-1.5 text-[11px] text-gray-500 font-semibold">
                                            {c.label}
                    </p>
                    <p className="text-sm font-bold text-gray-900 mt-1 whitespace-pre-wrap">{c.value}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Symptômes & signes */}
          {consultation.symptomes_signes && (
            <Section label="Symptômes & signes">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{consultation.symptomes_signes}</p>
            </Section>
          )}

          {/* Antécédents */}
          {consultation.antecedents_medicaux_connus && (
            <Section label="Antécédents médicaux connus">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{consultation.antecedents_medicaux_connus}</p>
            </Section>
          )}

          {/* Traitement en cours */}
          {hasTraitement && (
            <Section label="Traitement en cours">
              <div className="space-y-2">
                {medicamentsEnCours.length > 0 ? (
                  <div className="space-y-1.5">
                    {medicamentsEnCours
                      .filter((m) => m.nom?.trim())
                      .map((m, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-gray-400 text-xs mt-0.5">{i + 1}.</span>
                          <span className="text-sm text-gray-800">
                            {m.nom}
                            {m.dosage ? <span className="text-gray-500"> ({m.dosage})</span> : null}
                          </span>
                        </div>
                      ))}
                  </div>
                ) : consultation.traitement_en_cours ? (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{consultation.traitement_en_cours}</p>
                ) : null}

                {consultation.patient_regulier_medicaments != null && (
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      consultation.patient_regulier_medicaments
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <i className={`${consultation.patient_regulier_medicaments ? 'ri-checkbox-circle-fill' : 'ri-indeterminate-circle-line'} w-3.5 h-3.5 flex items-center justify-center`}></i>
                    {consultation.patient_regulier_medicaments
                      ? 'Patient régulier dans ses médicaments'
                      : 'Prise de médicaments irrégulière'}
                  </span>
                )}
              </div>
            </Section>
          )}

          {/* Examen clinique */}
          {consultation.examen && (
            <Section label="Examen clinique">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{consultation.examen}</p>
            </Section>
          )}

          {/* Diagnostic */}
          {consultation.diagnostic && (
            <Section label="Diagnostic">
              <p className="text-sm text-red-800 bg-red-50 rounded-lg p-3 whitespace-pre-wrap">{consultation.diagnostic}</p>
            </Section>
          )}

          {/* Prescription */}
          {hasPrescription && (
            <Section label="Prescription / Ordonnance">
              <div className="space-y-1.5">
                {medicamentsPrescrits
                  .filter((m) => m.nom?.trim())
                  .map((m, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-gray-400 text-xs mt-0.5">{i + 1}.</span>
                      <span className="text-sm text-gray-800">{formatLigneOrdonnance(m)}</span>
                    </div>
                  ))}
              </div>
            </Section>
          )}

          {/* Soins */}
          {hasSoins && (
            <Section label="Soins prescrits">
              <div className="space-y-1.5">
                {soins
                  .filter((s) => s.description?.trim())
                  .map((s, i) => (
                    <div key={i} className="flex items-start gap-2">
                                            <div>
                        <span className="text-sm text-gray-800">{s.description}</span>
                        {s.date && (
                          <span className="text-xs text-gray-400 ml-2">{new Date(s.date).toLocaleDateString('fr-FR')}</span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </Section>
          )}

          {/* Suivi */}
          {consultation.suivi_vers && (
            <Section label="Suivi / Orientation">
              <p className="text-sm text-gray-800">{consultation.suivi_vers}</p>
            </Section>
          )}

          {/* Prochain rendez-vous */}
          {consultation.prochain_rdv && (
            <Section label="Prochain rendez-vous">
              <p className="text-sm text-gray-800">
                {new Date(consultation.prochain_rdv).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </Section>
          )}

          {/* Mot de fin */}
          {consultation.mot_fin && (
            <Section label="Mot de fin">
              <p className="text-sm text-gray-800 italic whitespace-pre-wrap">{consultation.mot_fin}</p>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}